import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { DishCard } from '../components/DishCard';
import { MenuCardPrestige } from '../components/MenuCard';
import { MenuCardDinatoire } from '../components/MenuCardDinatoire';
import { MenuCardDesserts } from '../components/MenuCardDesserts';
import { MenuCardMenusPrestige } from '../components/MenuCardMenusPrestige';
import { MenuCardCarteEstivale } from '../components/MenuCardCarteEstivale';
import { MenuCardTraiteurAnnuel } from '../components/MenuCardTraiteurAnnuel';
import { PlanningSemaine } from '../components/PlanningSemaine';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { id: 'all', label: 'Plats du jour classiques' },
  { id: 'evenement', label: 'Événements' },
];

const PRESTIGE_CATEGORIES = ['menus_prestige', 'apero_prestige', 'apero_dinatoire', 'desserts_prestige'];

export default function MenuPage() {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchDishes = async () => {
      setLoading(true);
      try {
        let url;
        if (activeCategory === 'all' || activeCategory === 'evenement') {
          url = `${BACKEND_URL}/api/dishes`;
        } else {
          url = `${BACKEND_URL}/api/dishes?category=${activeCategory}`;
        }
        const response = await axios.get(url);
        let data = response.data;
        if (activeCategory === 'evenement') {
          data = data.filter((d) => PRESTIGE_CATEGORIES.includes(d.category));
        } else if (activeCategory === 'all') {
          data = data.filter(
            (d) => !PRESTIGE_CATEGORIES.includes(d.category)
              && d.category !== 'carte_estivale'
              && d.category !== 'evenements_traiteurs'
          );
        }
        setDishes(data);
      } catch (error) {
        console.error('Error fetching dishes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDishes();
  }, [activeCategory]);

  const filteredDishes = dishes;

  return (
    <main className="min-h-screen bg-transparent pt-24">
      {/* Header */}
      <section className="container-padding max-w-7xl mx-auto py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4 mb-12"
        >
          <span className="text-terracotta font-medium tracking-wide uppercase text-sm">
            Notre Carte
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-navy">
            Explorez nos saveurs
          </h1>
          <p className="text-navy/60 max-w-2xl mx-auto">
            Des recettes du monde entier, préparées avec des ingrédients frais et de qualité.
          </p>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-col items-center gap-3 mb-8">
          {/* Ligne 1 — Plats du jour & sous-catégories */}
          <div className="flex flex-wrap gap-2 justify-center">
            {categories
              .filter((c) => c.id !== 'evenement')
              .map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${
                    activeCategory === category.id
                      ? 'bg-terracotta text-white'
                      : 'bg-white text-navy hover:bg-terracotta/10'
                  }`}
                  data-testid={`filter-${category.id}`}
                >
                  {category.label}
                </button>
              ))}
          </div>
          {/* Ligne 2 — Événements & spéciaux (toujours terracotta) */}
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setActiveCategory('carte_estivale')}
              className="px-4 py-2 rounded-full font-medium text-sm transition-colors bg-terracotta text-white hover:bg-terracotta/90"
              data-testid="filter-carte_estivale"
            >
              Carte estivale
            </button>
            <button
              onClick={() => setActiveCategory('evenement')}
              className="px-4 py-2 rounded-full font-medium text-sm transition-colors bg-terracotta text-white hover:bg-terracotta/90"
              data-testid="filter-evenement"
            >
              Événements prestiges
            </button>
            <button
              onClick={() => setActiveCategory('evenements_traiteurs')}
              className="px-4 py-2 rounded-full font-medium text-sm transition-colors bg-terracotta text-white hover:bg-terracotta/90"
              data-testid="filter-evenements_traiteurs"
            >
              Événements traiteurs
            </button>
          </div>
        </div>

        {/* Planning de la semaine (uniquement pour Plats du jour classiques) */}
        {activeCategory === 'all' && !loading && <PlanningSemaine />}

        {/* Planification annuelle traiteur (statique) */}
        {activeCategory === 'evenements_traiteurs' && <MenuCardTraiteurAnnuel />}

        {/* Dishes Grid */}
        {activeCategory !== 'evenements_traiteurs' && activeCategory !== 'all' && (loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white/50 rounded-2xl h-80 animate-pulse" />
            ))}
          </div>
        ) : filteredDishes.length > 0 ? (
          activeCategory === 'apero_prestige' ? (
            <MenuCardPrestige dishes={filteredDishes} />
          ) : activeCategory === 'apero_dinatoire' ? (
            <MenuCardDinatoire dishes={filteredDishes} />
          ) : activeCategory === 'desserts_prestige' ? (
            <MenuCardDesserts dishes={filteredDishes} />
          ) : activeCategory === 'menus_prestige' ? (
            <MenuCardMenusPrestige dishes={filteredDishes} />
          ) : activeCategory === 'carte_estivale' ? (
            <MenuCardCarteEstivale dishes={filteredDishes} />
          ) : activeCategory === 'evenement' ? (
            <div className="space-y-20">
              {filteredDishes.some((d) => d.category === 'apero_dinatoire') && (
                <MenuCardDinatoire dishes={filteredDishes.filter((d) => d.category === 'apero_dinatoire')} />
              )}
              {filteredDishes.some((d) => d.category === 'apero_prestige') && (
                <MenuCardPrestige dishes={filteredDishes.filter((d) => d.category === 'apero_prestige')} />
              )}
              {filteredDishes.some((d) => d.category === 'menus_prestige') && (
                <MenuCardMenusPrestige dishes={filteredDishes.filter((d) => d.category === 'menus_prestige')} />
              )}
              {filteredDishes.some((d) => d.category === 'desserts_prestige') && (
                <MenuCardDesserts dishes={filteredDishes.filter((d) => d.category === 'desserts_prestige')} />
              )}
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {filteredDishes.map((dish, index) => (
                <motion.div
                  key={dish.dish_id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <DishCard dish={dish} showAddToCart={activeCategory !== 'all'} />
                </motion.div>
              ))}
            </motion.div>
          )
        ) : (
          <div className="text-center py-16">
            <p className="text-[#6D4C41] text-lg">
              Aucun plat trouvé pour cette recherche.
            </p>
          </div>
        ))}
      </section>
    </main>
  );
}
